"use client";

import React, { useMemo } from "react";
import { Avatar } from "antd";
import { cn } from "utils";
import { useSnapshot } from "valtio";
import type { StockCardProps } from "./types";
import { PortfolioManage } from "@/src/components/PortfolioManage";
import CheckBox from "@/src/components/CheckBox";
import { menuSearchSelection } from "@/src/store/menuSearchSelection";
import { PositionAssetTypeEnum } from "@/src/features/api/memberApi";
import { favoritesCenter } from "@/src/store";

/**
 * 股票卡片组件 - 用于显示股票/加密货币信息
 */
export const StockCard: React.FC<StockCardProps> = ({
  data,
  onClick,
  className,
  originType,
}) => {
  const { selectIds, updateIds } = useSnapshot(menuSearchSelection);
  // 获取主题标签
  const getThemeTag = () => {
    if (data?.themes && data?.themes.length > 0) {
      return data?.themes[0]; // 显示第一个主题
    }
    return null;
  };

  const themeTag = getThemeTag();

  const handleCheck = () => {
    if (originType === "portfolio") {
      updateIds({
        asset_id: data.id,
        asset_type: data.type as PositionAssetTypeEnum,
      });
    } else {
      onClick?.();
    }
  };

  const checked = useMemo(() => {
    if (originType === "portfolio") {
      return !!favoritesCenter.state.allFavoriteData?.data?.[data.id || ""]
        ?.favoriteStates?.length;
    }
    return false;
  }, [data.id, originType]);
  return (
    <div
      data-openreplay-label="SearchResult · StockCard · div"
      className={cn(
        "flex items-center gap-3 p-4 md:pl-4 pl-0 rounded-3 cursor-pointer transition-colors",
        "md:hover:bg-background_white_overlay5",
        className,
      )}
      onClick={handleCheck}
    >
      {originType === "portfolio" ? (
        <CheckBox
          checked={
            selectIds.some(
              (item) =>
                item.asset_id === data.id &&
                item.asset_type === (data.type as PositionAssetTypeEnum),
            ) || checked
          }
          disabled={checked}
          iconSize={12}
        />
      ) : (
        <PortfolioManage
          id={data.id}
          type={data.type as "TOKEN" | "STOCK"}
          zIndex={999}
        />
      )}
      <div className="flex items-center flex-1 gap-2">
        <Avatar src={data.image} size={40} />
        <div className="flex flex-col flex-1 justify-center">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-1">
              <span className="body_medium_m text-content_primary whitespace-nowrap md:max-w-none max-w-[120px] truncate">
                {data?.short_name
                  ? `${data?.short_name} (${data.symbol})`
                  : data.symbol}
              </span>
              {themeTag && (
                <div className="px-1 py-0.5 rounded-1 border border-border_white_transparent20 body_bold_xs text-content_secondary">
                  {themeTag}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center w-full">
            <span className="body_s text-content_secondary whitespace-nowrap md:max-w-none max-w-[220px] truncate">
              {data.type === "STOCK" ? data.desc : data.name}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
